import * as React from 'react'
import {ReactNode} from 'react'
import {connect} from 'react-redux'
import {Col, Form, FormGroup, Input, Label, Row} from "reactstrap";
import {WithTranslation, withTranslation} from "react-i18next";
import {
    ContactType,
    DEFAULT_COUNTRIES, Language, NATURAL_SORT_COMPARE_OPTIONS,
    VALUE_ERROR_SUFFIX,
    VALUE_NORMALIZED_SUFFIX
} from "App/constants";
import {buildFormField, ContactData, contactFormChange, FormField} from "App/contactData";
import {UserState} from "App/user";
import {Map} from 'immutable';
import {InputType} from "reactstrap/lib/Input";
import {ApplicationState} from "App/store";
import * as EmailValidator from 'email-validator';
import {asBoolean, asString, selectedLang} from "App/helper";
import PhoneInput from 'react-phone-number-input'
import {isValidPhoneNumber} from 'react-phone-number-input'
import {LocalizedCountryNames} from "i18n-iso-countries";
import {countriesByLanguage} from "App/country";

interface ComponentProps {
    showLabel: Map<string, boolean>
}

interface StateToProps {
    contactData: ContactData;
    type: ContactType;
    user: UserState | null;
}

interface DispatchToProps {
    onContactChange: (data: Object) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
    onContactChange: (data: Object) => dispatch(contactFormChange(data))
});

const mapStateToProps = (state: ApplicationState, props: StateToProps): ComponentProps => ({
    showLabel: computeInputLabels(state.contactData.contact),
});

function computeInputLabels(form: Object): Map<string, boolean> {
    var showLabel: Map<string, boolean> = Map<string, boolean>();
    for (let field in FormField) {
        if (form[FormField[field]]) {
            showLabel = showLabel.set(FormField[field], true);
        }
    }
    return showLabel;
}

function isFieldInvalid(field: FormField, form: Object): boolean {
    return !!form[field + VALUE_ERROR_SUFFIX];
}

function isFieldMandatory(type: ContactType, form: Object, field: FormField, user: UserState | null): boolean {
    switch (field) {
        case FormField.EMAIL:
            return type === ContactType.CONTRACT || type === ContactType.SUBSCRIBE || form[FormField.ACCOUNTREQUEST] || !form[FormField.PHONE] || isFieldInvalid(FormField.PHONE, form);
        case FormField.PHONE:
            return type === ContactType.CONTRACT || type === ContactType.SUBSCRIBE || form[FormField.ACCOUNTREQUEST] || !form[FormField.EMAIL] || isFieldInvalid(FormField.EMAIL, form);
        case FormField.TITLE:
            return type === ContactType.CONTRACT;
        case FormField.FAMILYNAME:
        case FormField.GIVENNAME:
        case FormField.COMPANYNAME:
            return type === ContactType.CONTRACT || type === ContactType.SUBSCRIBE || form[FormField.ACCOUNTREQUEST];
        case FormField.ADDRESS1:
        case FormField.ZIPCODE:
        case FormField.CITY:
        case FormField.COUNTRY:
            return type === ContactType.CONTRACT && !user;
        case FormField.ADDRESS2:
        case FormField.COMMENTS:
        case FormField.ACCOUNTREQUEST:
            return false;
        default:
            return true;
    }
}

function isFieldVisible(field: FormField, user: UserState | null): boolean {
    switch (field) {
        case FormField.ADDRESS1:
        case FormField.ADDRESS2:
        case FormField.ZIPCODE:
        case FormField.CITY:
        case FormField.COUNTRY:
        case FormField.ACCOUNTREQUEST:
            return !user;
        default:
            return true;
    }
}

function validateFieldValue(props: ContactFormValidationProps, field: FormField, value: string | boolean): string | undefined {
    const {t} = props;
    switch (field) {
        case FormField.EMAIL:
            return value && !EmailValidator.validate(asString(value)) ? t('products.configuration.invalidemail') :
                validateMandatoryField(props, field, asString(value));
        case FormField.PHONE:
            return value && !isValidPhoneNumber(value) ? t('products.configuration.invalidphone') :
                validateMandatoryField(props, field, asString(value));
        case FormField.ACCOUNTREQUEST:
            return undefined;
        default:
            return validateMandatoryField(props, field, asString(value));
    }
}

function validateMandatoryField(props: ContactFormValidationProps, field: FormField, value: string): string | undefined {
    const {t, contactData, type, user} = props;
    return isFieldMandatory(type, contactData.contact, field, user) && !value ? t('products.configuration.missigvalue') : undefined;
}

class _Component extends React.PureComponent<StateToProps & ComponentProps & DispatchToProps & WithTranslation> {

    constructor(props: StateToProps & ComponentProps & DispatchToProps & WithTranslation, context: any) {
        super(props, context);
        this.formField = this.formField.bind(this);
        this.fieldData = this.fieldData.bind(this);
        this.reportField = this.reportField.bind(this);
    }

    /**
     * Utility to build the a form field
     * @param field
     * @param inputType
     * @param rowCount
     */
    formField(field: FormField, inputType: InputType, rowCount?: number): ReactNode {
        const {t, i18n, user, contactData, type, onContactChange, showLabel} = this.props;
        const label: string = t('products.configuration.' + field);
        const invalid: boolean = isFieldInvalid(field, contactData.contact);
        const mandatory: boolean = isFieldMandatory(type, contactData.contact, field, user);
        const language: Language = selectedLang(i18n);
        const countries: LocalizedCountryNames = countriesByLanguage(language);

        return <React.Fragment>
            {invalid ?
                <label htmlFor={field}
                       className={"form-label mb-1 show error"}>{contactData.contact[field + VALUE_ERROR_SUFFIX]}</label> :
                inputType === 'checkbox' ?
                    null :
                    <label htmlFor={field}
                           className={"form-label mb-1 " + (showLabel.get(field) ? 'show' : '')}>{label}</label>
            }
            {
                field === FormField.PHONE ?
                    <PhoneInput id={field} name={field}
                                placeholder={t('products.configuration.field' + (mandatory ? 'required' : 'optional'), {field: label})}
                                className={"form-control text-normal-details form-control-lg " + (invalid ? "field-error" : "")}
                                readOnly={!!(user && user[field])}
                                value={asString(contactData.contact[field])}
                                country={contactData.contact[FormField.COUNTRY]}
                                countryOptions={DEFAULT_COUNTRIES}
                                locale={language}
                                onChange={(value: string) => onContactChange(this.fieldData(field, value))}/> :
                    field === FormField.COUNTRY ?
                        <Input type="select" id={field} name={field}
                               placeholder={t('products.configuration.field' + (mandatory ? 'required' : 'optional'), {field: label})}
                               className={"form-control text-normal-details form-control-lg " + (invalid ? "field-error" : "")}
                               readOnly={!!(user && user[field])}
                               value={asString(contactData.contact[field])}
                               required={true}
                               onChange={(e) => onContactChange(this.fieldData(field, e.target.value))}>
                            <option hidden disabled selected value="" className={"select-placeholder"}>{t('products.configuration.field' + (mandatory ? 'required' : 'optional'), {field: label})}</option>
                            {
                                Object.keys(countries)
                                    .sort((a, b) => countries[a].localeCompare(countries[b], undefined, NATURAL_SORT_COMPARE_OPTIONS))
                                    .map((key: string) => (
                                        <option key={key}
                                            value={key}>{countries[key]}</option>
                                ))
                            }
                        </Input> :
                        inputType === 'textarea' ?
                            <textarea id={field} name={field}
                                      placeholder={t('products.configuration.field' + (mandatory ? 'required' : 'optional'), {field: label})}
                                      className={"form-control text-normal-details form-control-lg " + (invalid ? "field-error" : "")}
                                      readOnly={!!(user && user[field])}
                                      value={asString(contactData.contact[field])}
                                      onChange={(e) => onContactChange(this.fieldData(field, e.target.value))}
                                      rows={rowCount}/> :
                            inputType === 'checkbox' ?
                                <React.Fragment>
                                    <Input type={'checkbox'} id={field} name={field} className={""}
                                           checked={asBoolean(contactData.contact[field])}
                                           onChange={(e) => onContactChange(this.fieldData(field, e.target.checked))}/>
                                    <Label className="form-check-label" htmlFor={field}>{label}</Label>
                                </React.Fragment> :
                                <Input type={inputType} id={field} name={field}
                                       placeholder={t('products.configuration.field' + (mandatory ? 'required' : 'optional'), {field: label})}
                                       className={"form-control text-normal-details form-control-lg " + (invalid ? "field-error" : "")}
                                       readOnly={!!(user && user[field])}
                                       value={asString(contactData.contact[field])}
                                       onChange={(e) => onContactChange(this.fieldData(field, e.target.value))}/>
            }
        </React.Fragment>
    }

    fieldData(field: FormField, value: string | boolean): Object {
        const {t, contactData, type} = this.props;
        const error: string | undefined = validateFieldValue(this.props, field, value);
        return {
            ...buildFormField(field, value),
            // Add error part
            [field + VALUE_ERROR_SUFFIX]: error,
            // This next part is bad
            ...(field === FormField.EMAIL && type === ContactType.OFFER ? {
                [FormField.PHONE + VALUE_ERROR_SUFFIX]: value || contactData.contact[FormField.PHONE] ? undefined : t('products.configuration.missigvalue')
            } : field === FormField.PHONE && type === ContactType.OFFER ? {
                [FormField.EMAIL + VALUE_ERROR_SUFFIX]: value || contactData.contact[FormField.EMAIL] ? undefined : t('products.configuration.missigvalue')
            } : field === FormField.ACCOUNTREQUEST && !value && type === ContactType.OFFER ? {
                ...(!contactData.contact[FormField.PHONE] ? {[FormField.PHONE + VALUE_ERROR_SUFFIX]: undefined} : {}),
                ...(!contactData.contact[FormField.EMAIL] ? {[FormField.EMAIL + VALUE_ERROR_SUFFIX]: undefined} : {}),
                ...(!contactData.contact[FormField.FAMILYNAME] ? {[FormField.FAMILYNAME + VALUE_ERROR_SUFFIX]: undefined} : {}),
                ...(!contactData.contact[FormField.GIVENNAME] ? {[FormField.GIVENNAME + VALUE_ERROR_SUFFIX]: undefined} : {}),
                ...(!contactData.contact[FormField.COMPANYNAME] ? {[FormField.COMPANYNAME + VALUE_ERROR_SUFFIX]: undefined} : {}),
            } : {})
            // End bad part
        }
    }

    reportField(field: FormField): ReactNode {
        const {t, contactData} = this.props;
        const value: any = contactData.contact[field + VALUE_NORMALIZED_SUFFIX];
        return value ?
            field === FormField.ACCOUNTREQUEST ?
                <Row className="no-gutters pt-2">
                    <Col className={""}><Label className={"report-label"}>{t('products.configuration.' + field)}</Label></Col>
                </Row> :
                <Row className="no-gutters pt-2">
                    <Col className={"col-sm-3"}><Label
                        className={"report-label"}>{t('products.configuration.' + field)}</Label></Col>
                    <Col className={"col-sm-9 text-normal-details text-pre-wrap"}>{value}</Col>
                </Row> :
            null
    }

    render() {
        const {type, contactData, t, user} = this.props;
        return <React.Fragment>
            <Row className="no-gutters pt-2">
                <Label>{t(contactData.formSubmitted ? 'products.configuration.submitted' + type + 'text' : 'products.configuration.request' + type + 'text')}</Label>
            </Row>
            {contactData.formSubmitted ?
                <React.Fragment>
                    {this.reportField(FormField.EMAIL)}
                    {this.reportField(FormField.PHONE)}
                    {this.reportField(FormField.TITLE)}
                    {this.reportField(FormField.FAMILYNAME)}
                    {this.reportField(FormField.GIVENNAME)}
                    {this.reportField(FormField.COMMENTS)}
                    {this.reportField(FormField.COMPANYNAME)}
                    {user ?
                        null :
                        <React.Fragment>
                            {this.reportField(FormField.ADDRESS1)}
                            {this.reportField(FormField.ADDRESS2)}
                            {this.reportField(FormField.ZIPCODE)}
                            {this.reportField(FormField.CITY)}
                            {this.reportField(FormField.COUNTRY)}
                            {contactData.subscribed || !this.reportField(FormField.ACCOUNTREQUEST) ?
                                null :
                                this.reportField(FormField.ACCOUNTREQUEST)
                            }
                        </React.Fragment>
                    }
                </React.Fragment> :
                <Form>
                    <Row className="no-gutters">
                        <FormGroup
                            className={"col-sm-6 pr-sm-2 mb-0"}>{this.formField(FormField.EMAIL, "email",)}</FormGroup>
                        <FormGroup
                            className={"col-sm-6 pl-sm-2 mb-0"}>{this.formField(FormField.PHONE, "tel",)}</FormGroup>
                    </Row>
                    <Row className="no-gutters">
                        <FormGroup
                            className={"col-sm-3 pr-sm-2 mb-0"}>{this.formField(FormField.TITLE, "text",)}</FormGroup>
                        <FormGroup
                            className={"col-sm-5 pl-sm-2 mb-0"}>{this.formField(FormField.FAMILYNAME, "text",)}</FormGroup>
                        <FormGroup
                            className={"col-sm-4 pl-sm-2 mb-0"}>{this.formField(FormField.GIVENNAME, "text",)}</FormGroup>
                    </Row>
                    {user ?
                        null :
                        <React.Fragment>
                            <FormGroup className={"mb-0"}>{this.formField(FormField.COMPANYNAME, "text",)}</FormGroup>
                            <FormGroup className={"mb-0"}>{this.formField(FormField.ADDRESS1, "text",)}</FormGroup>
                            <FormGroup className={"mb-0"}>{this.formField(FormField.ADDRESS2, "text",)}</FormGroup>
                            <Row className="no-gutters">
                                <FormGroup
                                    className={"col-sm-3 pr-sm-2 mb-0"}>{this.formField(FormField.ZIPCODE, "number",)}</FormGroup>
                                <FormGroup
                                    className={"col-sm-5 pl-sm-2 mb-0"}>{this.formField(FormField.CITY, "text",)}</FormGroup>
                                <FormGroup
                                    className={"col-sm-4 pl-sm-2 mb-0"}>{this.formField(FormField.COUNTRY, "select",)}</FormGroup>
                            </Row>
                        </React.Fragment>
                    }
                    <FormGroup className="mb-1">{this.formField(FormField.COMMENTS, "textarea", 5)}</FormGroup>
                    <Label>{t('products.configuration.mandatoryhint')}</Label>
                    {user || contactData.subscribed || type === ContactType.SUBSCRIBE ?
                        null :
                        <FormGroup
                            className={"form-check"}>{this.formField(FormField.ACCOUNTREQUEST, "checkbox")}</FormGroup>
                    }
                </Form>
            }
        </React.Fragment>
    }
}

export interface ContactFormStateProps extends StateToProps {
}

export interface ContactFormValidationProps extends ContactFormStateProps, WithTranslation, DispatchToProps {
}

export function computeAllowSubmit(props: ContactFormStateProps): boolean {
    const {contactData, user} = props;
    return Object.keys(FormField).map(key => FormField[key]).reduce((prev: boolean, curr: FormField) =>
        prev && (!isFieldVisible(curr, user) || !isFieldInvalid(curr, contactData.contact)), true)
}

export function validateForm(props: ContactFormValidationProps): boolean {
    const {contactData, onContactChange} = props;
    var validationObject: object =
        Object.keys(FormField).map(key => FormField[key]).reduce((prev: Object, curr: FormField) => {
            var value: string | boolean | undefined = contactData.contact[curr];
            const error: string | undefined = validateFieldValue(props, curr, value && value.toString() || '');
            return error ? {...prev, [curr + VALUE_ERROR_SUFFIX]: error} : prev;
        }, {});
    onContactChange(validationObject);
    return Object.keys(validationObject).length === 0;
}

export const ContactForm = connect(mapStateToProps, mapDispatchToProps)(withTranslation('common')(_Component));


// WEBPACK FOOTER //
// ./src/components/ContactForm.tsx